

import { makeStyles } from 'tss-react/mui';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ErrorInfo,
  HtmlTrans,
  Typography,
  useSnackbarHandler,
} from 'web-library';
import { useDeleteExamFee } from '../../../../api-refactored/exam-configuration/useDeleteExamFee';
import { ExamFeeModelResponse } from '../../../../api-refactored/exam-configuration/usePostExamFeeListing';

const useStyles = makeStyles()((theme) =>
({
  actionContainer: {
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
}));

interface RemoveModalProps {
  isModalOpened: boolean;
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  fetchExamFeeListing: () => void;
  isAeisExam: boolean;
  defaultData?: ExamFeeModelResponse;
}

const RemoveModal = ({
  isModalOpened,
  setModalOpened,
  fetchExamFeeListing,
  isAeisExam,
  defaultData,
}: RemoveModalProps) => {
  const { t } = useTranslation();
  const { snackbarHandler } = useSnackbarHandler();
  const { classes } = useStyles();
  const [apiErrorMsg, setApiErrorMsg] = useState<string | null>(null);

  const {
    mutate: deleteExamFeeMutate,
    status: deleteExamFeeStatus,
    reset: deleteExamFeeReset,
  } = useDeleteExamFee({
    onSuccess: () => {
      fetchExamFeeListing();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: isAeisExam
          ? t('exam->>ExamFeeConfiguration.AeisFeeRemoved', {
            admissionLevel: defaultData?.admissionLevelDescription,
          })
          : t('exam->>ExamFeeConfiguration.SpersRemoved'),
      });
      onDialogCloseHandler();
    },
    onError: () => {
      setApiErrorMsg(t('components->>Error.DefaultErrorMsg'));
    },
  });

  const onDeleteHandler = () => {
    setApiErrorMsg(null);
    deleteExamFeeMutate({ examFeeUuid: defaultData?.uuid || '' });
  };

  const onDialogCloseHandler = () => {
    deleteExamFeeReset();
    setApiErrorMsg(null);
    setModalOpened(false);
  };

  return (
    <Dialog id="remove-exam-fee-modal" open={isModalOpened} maxWidth="md">
      <DialogTitle>{t('exam->>ExamFeeConfiguration.RemoveTitle')}</DialogTitle>

      <DialogContent>
        {isAeisExam ? (
          <Typography variant="body1">
            <HtmlTrans
              i18nKey="exam->>ExamFeeConfiguration.RemoveContentAeis"
              values={{ admissionLevel: defaultData?.admissionLevelDescription }}
            />
          </Typography>
        ) : (
          <Typography variant="body1">
            {t('exam->>ExamFeeConfiguration.RemoveContentSpers')}
          </Typography>
        )}

        {apiErrorMsg && <ErrorInfo label={apiErrorMsg} />}
      </DialogContent>
      <DialogActions>
        <Box className={classes.actionContainer}>
          <Button
            id="button-spe-cancel-remove-exam-fees"
            type="button"
            variant="text"
            onClick={onDialogCloseHandler}
          >
            {t('exam->>ExamConfiguration.Cancel')}
          </Button>
          <ApiButton
            id="button-spe-proceed-remove-exam-fees"
            status={deleteExamFeeStatus}
            onClick={onDeleteHandler}
          >
            {t('exam->>ExamConfiguration.Proceed')}
          </ApiButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveModal;
